import {khanFetch} from "@khan/data-fetch";
import {emitMessageSent} from "@khan/events-guide";
import type {
    AIGuideStreamHandlers,
    GuideState,
    ImageInput,
    UserPersona,
} from "@khan/guide-types";
import {Errors, KAError} from "@khan/logging";

import {processStreamedAIResponse} from "./process-streamed-ai-response.ts";

export interface StreamingChatInput {
    persona: UserPersona | "";
    // TODO(amorelli,CL-958): Update name to "messageType"
    command: string;
    customPrompt: string;
    contentUrlPath?: string;
    url: string;
    region?: string;
    contextualLinks: GuideState["contextualLinks"];
    conversation: GuideState["conversation"];
    promptStore: Record<string, string>;
    thread_id: GuideState["threadId"];
    dynamicBot: GuideState["dynamicBot"] | null | undefined;
    botName: GuideState["selectedBotName"] | null | undefined;
    event:
        | {
              name: string;
              params: Record<any, any>;
          }
        | null
        | undefined;
    // TODO(amorelli,CL-958): Update name to "command"
    commandObject:
        | {
              name: string;
              params: Record<any, any>;
          }
        | null
        | undefined;
    entryComponent?: {name: string; input: Record<string, any>};
    message: string | null | undefined;
    imageInputs: Array<ImageInput> | undefined;

    // TODO(KORE-1162) Remove after we're fully committed to ImageInput
    imageURLs: Array<string> | undefined;

    actionBubble: string | null | undefined;
    modelOverride: string | null | undefined;
    temperatureOverride: number | null | undefined;
    stream: boolean;
    flags: Record<string, string>;
    sendToPython: boolean;
    annotations?: ReadonlyArray<string>;
    generateVoice?: boolean;
    assignmentId?: string;
    selectedVoice?: string;
    selectedGuideLanguageCode: string;

    // TODO(amorelli): May want to move to a more generic approach for passing
    // in input specific to a small set of use cases
    essaySessionId?: string;
    isClassroom?: boolean;
}

/**
 * askAIGuide makes a request to a streaming chat API. It invokes the `onRead`
 * callback as it processes the streamed response and returns the buffered
 * result as a promise.
 */
export const askAIGuide = async (
    input: StreamingChatInput,
    signal: AbortSignal | null | undefined,
    handlers: AIGuideStreamHandlers,
    onResponseStart?: null | (() => void),
): Promise<void> => {
    const {command, commandObject, event, botName} = input;

    // Add query parameter to the streaming-chat call so we can easily tell in
    // the logs what the call is generally doing.
    const params = new URLSearchParams({messageType: command});

    if (event) {
        params.append("event", event.name);
    }

    if (commandObject) {
        params.append("command", commandObject.name);
    }

    if (botName) {
        params.append("config", botName);
    }

    try {
        // NOTE (DW): Right now useMessageSentListener is only being used to
        // implement CEDAR events in the WritingCoach, and we're only interested
        // in when the user sends a message. Perhaps this can/should change as
        // people use that event hook more?
        if (command === "chat_message") {
            emitMessageSent({threadID: input.thread_id ?? ""});
        }
        const resp = await khanFetch(
            "/api/internal/_ai-guide/streaming-chat?" + params.toString(),
            {
                method: "POST",
                body: JSON.stringify(input),
                signal,
            },
        );
        if (!resp.ok || !resp.body) {
            throw new KAError("askAIGuide request failed", Errors.KhanService);
        }

        // We will process the response as it streams in and will resolve the final
        // result (buffered response)
        const reader = resp.body.getReader();
        await processStreamedAIResponse(reader, handlers, onResponseStart);
    } catch (err: any) {
        // We handle the exception thrown from within the mutation success
        // handler.
        if (err instanceof KAError) {
            throw err;
        }

        // Otherwise we throw a generic error.
        throw new KAError(
            "an error occurred during a request to askAIGuide",
            Errors.Internal,
            {cause: err},
        );
    }
};
